import { Alert, Button, Stack } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useGetProfileQuery } from '../../../api/profile'
import { useLazyGetRecommendedProjectsForMeQuery, useRecordRecommendationInteractionMutation } from '../../../api/recommendations'
import { useCreateProjectJoinRequestMutation } from '../../../api/projects'
import {
  RecommendationsLoadMoreTrigger,
  RecommendationProjectCard,
  type RecommendationProjectCardMatch,
  RecommendationSection,
  RecommendationsEmpty,
  RecommendationsError,
  RecommendationsLoading,
} from './RecommendationCards'
import type { RecommendationProjectMatch, RecommendationTier } from '../../../api/types'
import { extractApiErrorMessage } from '../../../utils/api'
import { toast } from 'react-toastify'

const PAGE_SIZE = 20

function mergeMatches(base: RecommendationProjectMatch[], next: RecommendationProjectMatch[]): RecommendationProjectMatch[] {
  const seen = new Set(base.map((match) => match.slot.id))
  return [...base, ...next.filter((match) => {
    if (seen.has(match.slot.id)) return false
    seen.add(match.slot.id)
    return true
  })]
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)))
}

function sortProjectMatches(matches: RecommendationProjectMatch[]): RecommendationProjectMatch[] {
  return [...matches].sort((left, right) => {
    if (left.tier !== right.tier) {
      return left.tier === 'exact' ? -1 : 1
    }

    return right.score - left.score
  })
}

function groupByProject(matches: RecommendationProjectMatch[]): RecommendationProjectCardMatch[] {
  const groups = new Map<number, RecommendationProjectMatch[]>()

  matches.forEach((match) => {
    const projectMatches = groups.get(match.project.id) ?? []
    projectMatches.push(match)
    groups.set(match.project.id, projectMatches)
  })

  return Array.from(groups.values()).map((projectMatches) => {
    const sorted = sortProjectMatches(projectMatches)
    const best = sorted[0]

    const tier: RecommendationTier = sorted.some((match) => match.tier === 'exact') ? 'exact' : 'potential'

    return {
      ...best,
      tier,
      score: Math.max(...sorted.map((match) => match.score)),
      reasons: uniqueStrings(sorted.flatMap((match) => match.reasons)),
      missing_fields: uniqueStrings(sorted.flatMap((match) => match.missing_fields)),
      matches: sorted,
    }
  }).sort((left, right) => {
    if (left.tier !== right.tier) {
      return left.tier === 'exact' ? -1 : 1
    }

    return right.score - left.score
  })
}

export const RecommendationsForMePage = () => {
  const { data: profile } = useGetProfileQuery()
  const [loadRecommendations, { isLoading, isFetching, isError }] = useLazyGetRecommendedProjectsForMeQuery()
  const [recordInteraction] = useRecordRecommendationInteractionMutation()
  const [createJoinRequest] = useCreateProjectJoinRequestMutation()
  const [joiningProjectId, setJoiningProjectId] = useState<number | null>(null)
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set())
  const [exactItems, setExactItems] = useState<RecommendationProjectMatch[]>([])
  const [potentialItems, setPotentialItems] = useState<RecommendationProjectMatch[]>([])
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  const matchKey = (match: RecommendationProjectMatch) => String(match.slot.id)
  const groupMatches = (match: RecommendationProjectCardMatch) => match.matches ?? [match]
  const hideMatch = (match: RecommendationProjectCardMatch) => setHiddenKeys((current) => {
    const next = new Set(current)
    groupMatches(match).forEach((item) => next.add(matchKey(item)))
    return next
  })
  const restoreMatch = (match: RecommendationProjectCardMatch) => {
    setHiddenKeys((current) => {
      const next = new Set(current)
      groupMatches(match).forEach((item) => next.delete(matchKey(item)))
      return next
    })
  }

  const loadPage = useCallback(async (cursor: string | null) => {
    const response = await loadRecommendations({ limit: PAGE_SIZE, cursor }, true).unwrap()
    setExactItems((current) => cursor ? mergeMatches(current, response.exact) : response.exact)
    setPotentialItems((current) => cursor ? mergeMatches(current, response.potential) : response.potential)
    setMissingFields(response.missing_fields ?? [])
    setNextCursor(response.meta?.next_cursor ?? null)
    setHasLoadedOnce(true)
  }, [loadRecommendations])

  useEffect(() => {
    void loadPage(null)
  }, [loadPage])

  const visibleItems = [...exactItems, ...potentialItems].filter((match) => !hiddenKeys.has(matchKey(match)))
  const groupedItems = groupByProject(visibleItems)
  const exact = groupedItems.filter((match) => match.tier === 'exact')
  const potential = groupedItems.filter((match) => match.tier !== 'exact')
  const isEmpty = groupedItems.length === 0

  const dismiss = async (match: RecommendationProjectCardMatch) => {
    if (!profile?.id) return

    hideMatch(match)
    try {
      await Promise.all(groupMatches(match).map((item) => recordInteraction({
          direction: 'for_user',
          context_type: 'user',
          context_id: profile.id,
          target_type: 'project_need_slot',
          target_id: item.slot.id,
          action: 'dismissed',
        }).unwrap(),
      ))
    } catch {
      restoreMatch(match)
      toast.error('Не удалось скрыть рекомендацию')
    }
  }

  const act = async (match: RecommendationProjectCardMatch) => {
    if (!profile?.id || !match.project.id) return

    setJoiningProjectId(match.project.id)
    try {
      await createJoinRequest({ projectId: match.project.id }).unwrap()
      toast.success('Заявка отправлена')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось отправить заявку')
    } finally {
      setJoiningProjectId(null)
    }
  }

  if (isLoading || (isFetching && !hasLoadedOnce)) {
    return <RecommendationsLoading />
  }

  if (isError) {
    return <RecommendationsError />
  }

  return (
    <Stack gap={2.5}>
      {missingFields.length > 0 && (
        <Alert severity="info">Рекомендации станут точнее, когда профиль, компетенции, ресурсы и формат участия заполнены подробнее.</Alert>
      )}

      {isEmpty ? (
        <RecommendationsEmpty message="Пока нет подходящих проектов" />
      ) : (
        <>
          <RecommendationSection
            title="Точные совпадения"
            items={exact}
            renderItem={(match) => (
              <RecommendationProjectCard
                key={`exact-${match.project.id}`}
                match={match}
                isDismissing={false}
                onDismiss={() => dismiss(match)}
                primaryAction={
                  <Button variant="follow" size="small" disabled={joiningProjectId === match.project.id} onClick={() => act(match)}>
                    Откликнуться
                  </Button>
                }
              />
            )}
          />
          <RecommendationSection
            title="Могут подойти"
            items={potential}
            renderItem={(match) => (
              <RecommendationProjectCard
                key={`potential-${match.project.id}`}
                match={match}
                isDismissing={false}
                onDismiss={() => dismiss(match)}
                primaryAction={
                  <Button variant="follow" size="small" disabled={joiningProjectId === match.project.id} onClick={() => act(match)}>
                    Откликнуться
                  </Button>
                }
              />
            )}
          />
          <RecommendationsLoadMoreTrigger
            hasMore={Boolean(nextCursor)}
            isLoading={isFetching}
            onLoadMore={() => {
              if (nextCursor) void loadPage(nextCursor)
            }}
          />
        </>
      )}
    </Stack>
  )
}
